import CancelIcon from '@mui/icons-material/Cancel';
import { Box, Card, CardContent, CircularProgress, Divider, FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { type MaxSummaryWithLocation } from '../../../../shared/types';
import { useAdminMaxSummaries } from '../../../hooks/useAdminMaxSummaries';

type GroupedSummaries = {
    locationName: string;
    locationDescription: string;
    summaries: MaxSummaryWithLocation[];
};

export const MaxSummaryList = () => {
    const [filter, setFilter] = useState<string>('');

    const { data: summaries = [], isFetched } = useAdminMaxSummaries();

    const grouped = useMemo(() => {
        const map = new Map<string, GroupedSummaries>();

        for (const summary of summaries) {
            const key = summary.locationName;

            if (!map.has(key)) {
                map.set(key, {
                    locationName: summary.locationName,
                    locationDescription: summary.locationDescription,
                    summaries: [],
                });
            }

            map.get(key)!.summaries.push(summary);
        }

        return Array.from(map.values());
    }, [summaries]);

    const getFilteredGroups = () => {
        if (filter.length > 0) {
            const filterRegex = new RegExp(filter, 'gi');

            return grouped.filter(({ locationName, locationDescription }) => {
                return `${locationName} ${locationDescription}`.match(filterRegex);
            });
        }
        return grouped;
    };

    return (
        <Box sx={{ height: '100%', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 3, maxWidth: (theme) => theme.breakpoints.values.md }}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel htmlFor="filter-max-summaries">Filter Summaries</InputLabel>
                        <OutlinedInput
                            id="filter-max-summaries"
                            fullWidth
                            label="Filter Summaries"
                            value={filter}
                            onChange={(event) => setFilter(event.target.value)}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton aria-label={'clear the filter'} edge="end" onClick={() => setFilter('')}>
                                        <CancelIcon />
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                    </FormControl>
                </Box>
                <Divider />
                <Box sx={{ height: '100%', overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {!isFetched && <CircularProgress />}
                    {isFetched && (
                        <Box sx={{ width: '100%', height: '100%', overflowY: 'scroll', p: 0 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 3 }}>
                                {getFilteredGroups().map((group) => (
                                    <Card key={group.locationName} variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                {group.locationName}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                {group.locationDescription}
                                            </Typography>
                                            <Divider sx={{ my: 1.5 }} />
                                            {group.summaries.map((summary, index) => {
                                                const { _id, localTime, results, runTime } = summary;

                                                return (
                                                    <Box key={_id.toString()}>
                                                        {index > 0 && <Divider sx={{ my: 2 }} />}
                                                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                                            <b>Forecast for:</b>
                                                            {` ${dayjs(localTime).format('MMM D, YYYY h:mm A')}`}
                                                        </Typography>
                                                        <Typography variant="body2" gutterBottom>
                                                            <b>Summary:</b> {results.summary}
                                                        </Typography>
                                                        <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
                                                            <b>Temperature:</b> {results.temperature}
                                                        </Typography>
                                                        <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
                                                            <b>Sky:</b> {results.sky}
                                                        </Typography>
                                                        <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
                                                            <b>Wind & Pressure:</b> {results.windAndPressure}
                                                        </Typography>
                                                        <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
                                                            <b>Visibility:</b> {results.visibility}
                                                        </Typography>
                                                        <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
                                                            <b>Precipitation:</b> {results.precipitation}
                                                        </Typography>
                                                        <Divider sx={{ my: 2 }} />
                                                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                                                            <b>Run time:</b>
                                                            {` ${dayjs(runTime).format('MMM D, YYYY h:mm A')}`}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};
